import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'
import PropTypes from 'prop-types'
import moment from 'moment'

import CircularProgress from '@material-ui/core/CircularProgress'
import ExpertBadge from './ExpertBadge'
import InfoIcon from '@components/Icon/Info'
import Checkbox from '@components/Checkbox'
import Pin from '@components/Icon/Pin'
import Tooltip from '@components/Tooltip/Tooltip'
import ExpertStatusIcon from '@components/ExpertStatusIcon'
import SherlockReason from '@components/SherlockReason'
import Spinner from '@components/spinner'
import { expertApi } from '@atheneum/api'
import {
  getVanityName,
  openExpertInLinkedin,
  sendMetric
} from '@atheneum/api/dist/lib'
import {
  AddToProcessingActionEnum,
  AddToProcessingExpertTypeEnum,
  sendAddToProcessingMetric
} from '../components/ExpertBulkCreate/helpers/expertBulkCreate.metrics'
import { v4 as uuidv4 } from 'uuid'

const CV_UPDATE_THRESHOLD = process.env.CV_UPDATE_THRESHOLD || 14

class ExpertSearchItem extends Component {
  static propTypes = {
    expert: PropTypes.object.isRequired,
    expertSearchStore: PropTypes.object.isRequired,
    modalStore: PropTypes.object,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    updateStatus: PropTypes.number,
    growlStore: PropTypes.object,
    expertBulkCreateStore: PropTypes.object
  }

  constructor(props) {
    super(props)

    this.state = {
      pinButtonPressed: false
    }

    this.bulkExpertCreateIdentifier = `PIN_EXPERT_${uuidv4()}`

    this.completedExpertsDispose = props.expertBulkCreateStore.observeCompletedExperts(
      async experts => {
        const foundExpert = experts.find(
          e => e.customIdentifier === this.bulkExpertCreateIdentifier
        )
        if (foundExpert) {
          const expertData = toJS(foundExpert.data)

          if (
            Number(expertData.id) &&
            props.expertSearchStore.openLinkedinOnPin
          ) {
            this.updateExpert(expertData) // Not awaited on purpose, it's extremely slow
          }
        }
      }
    )
  }

  componentWillUnmount() {
    this.completedExpertsDispose()
  }

  pinExpert = async e => {
    e.stopPropagation()
    e.preventDefault()
    const {
      expertSearchStore,
      expert,
      modalStore,
      growlStore,
      expertBulkCreateStore
    } = this.props
    if (expert.pinned) {
      this.setState((prevState, props) => {
        return {
          pinButtonPressed: false
        }
      })

      await expertSearchStore.unpinExpert(expert)
    } else {
      this.setState((prevState, props) => {
        return {
          pinButtonPressed: true
        }
      })

      /*let expertTypeMetricDimension = null
      if (Number(expert.id)) {
        expertTypeMetricDimension =
          AddToProcessingExpertTypeEnum.EXISTING_EXPERT
      } else {
        expertTypeMetricDimension = AddToProcessingExpertTypeEnum.PDL
      }

      sendAddToProcessingMetric(
        AddToProcessingActionEnum.EXPERT_SEARCH_PIN,
        expertTypeMetricDimension
      )*/

      expertSearchStore.expertsToPin.push({
        id: expert.id,
        bulkCreateCustomIdentifier: this.bulkExpertCreateIdentifier,
        data: expert
      })
    }
  }

  updateExpert = async expert => {
    const needsUpdating = this.expertNeedsUpdate(expert)
    if (needsUpdating) {
      if (getVanityName(expert))
        this.props.expertSearchStore.setExpertUpdatingStatus(expert.id)
      await openExpertInLinkedin(expert)
      sendMetric('update_profile.pin_button', 'update-profile')
    }
  }

  expertNeedsUpdate = expert => {
    return !(
      expert.cvUpdatedAt &&
      moment(expert.cvUpdatedAt).isAfter(
        moment().subtract(CV_UPDATE_THRESHOLD, 'days')
      )
    )
  }

  render() {
    const { expert, expertSearchStore, updateStatus } = this.props
    const type = expert.data ? 'expert' : 'es_record'
    const data = type === 'es_record' ? expert : expert.data
    let lastUpdated = 'Pin & Update'

    if (data.cv?.updated_at) {
      lastUpdated = `Last updated ${moment(data.cv.updated_at).format(
        'MMM D, YYYY'
      )}`
    }
    const needsUpdating = !(
      data.cv?.updated_at &&
      moment(data.cv.updated_at).isAfter(
        moment().subtract(CV_UPDATE_THRESHOLD, 'days')
      )
    )
    if (!needsUpdating) {
      lastUpdated = 'Pin'
    }

    let statusId = !!this.props.expert.expertStatusId
      ? this.props.expert.expertStatusId
      : this.props.expert.data.expertStatusId

    if (!this.state.pinButtonPressed) {
      return (
        <>
          <div className="cbox-container">
            <Checkbox
              value={expert.checked}
              onChange={() => (expert.checked = !expert.checked)}
              disabled={
                this.props.expertSearchStore.pinnedExperts.filter(
                  e => e.checked
                ).length +
                  this.props.expertSearchStore.experts.filter(e => e.checked)
                    .length >
                  24 && !expert.checked
              }
            />
          </div>
          <div
            className={`expert-search-item ${
              this.props.isSelected ? 'active' : ''
            }`}
            onClick={this.props.onClick}>
            <div
              className="pull-right"
              onClick={
                !expertSearchStore.loadMore && !this.state.pinButtonPressed
                  ? this.pinExpert
                  : undefined
              }>
              {this.props.expertSearchStore.sherlockLoading && (
                <CircularProgress
                  style={{
                    color: '#429bdc',
                    width: '1.3em',
                    height: '1.3em',
                    'margin-right': '1em'
                  }}
                />
              )}
              {this.props.expert.sherlock ? (
                <SherlockReason
                  sherlock={this.props.expert.sherlock}
                  iconOnly
                />
              ) : null}
              {!!updateStatus && (
                <div className="expert-update-spinner">
                  <Tooltip
                    tooltip={'Profile update in progress...'}
                    placement="top">
                    <div className="iconWrapper">
                      <Spinner inline smallMargin />
                    </div>
                  </Tooltip>
                </div>
              )}
              <ExpertStatusIcon expertStatusId={statusId} expertSearch />
              <Pin
                active={!!this.props.expert.pinned}
                needsUpdating={needsUpdating}
                lastUpdated={lastUpdated}
              />
            </div>

            <div>
              <span className="name">
                {data.title} {`${data.firstName} ${data.lastName}`}
              </span>
              &nbsp;
              <span>{data.country}</span>&nbsp;
              {data.badgeId ? <ExpertBadge badgeId={data.badgeId} /> : ''}&nbsp;
              {data.highlight ? (
                <Tooltip
                  tooltip={
                    <div
                      className="expert-search-item__tooltip"
                      dangerouslySetInnerHTML={{
                        __html: data.highlight.industryExpertise
                      }}
                    />
                  }>
                  <div className="iconWrapper">
                    <InfoIcon />
                  </div>
                </Tooltip>
              ) : (
                ''
              )}
            </div>
            <RenderExperience data={data} />
          </div>
        </>
      )
    } else {
      return null
    }
  }
}

function RenderExperience({ data }) {
  if (data.experience && data.experience.company && data.experience.position) {
    return (
      <div className="experience">
        <div>{data.experience.position},</div>
        <div>
          <div className="pull-right">
            ({data.experience.start} - {data.experience.end})
          </div>
          <div>{data.experience.company}</div>
        </div>
      </div>
    )
  }

  if (data.company) {
    return (
      <div className="experience">
        <div>{data.position},</div>
        <div>
          <div>{data.company}</div>
        </div>
      </div>
    )
  }

  return <span>No experience</span>
}

export default inject(
  'expertSearchStore',
  'growlStore',
  'expertBulkCreateStore'
)(observer(ExpertSearchItem))
